import { motion } from "framer-motion";
import { BadgeCheck, FlaskConical, FileCheck2, Download, ShieldCheck } from "lucide-react";
import { Reveal, MaskLineInView } from "./Reveal";

const RESULTS = [
  { k: "Overall migration", v: "2.35 mg/L", limit: "≤ 60 mg/L" },
  { k: "Colour migration", v: "None observed", limit: "≤ 2 units" },
  { k: "Heavy metals (Ba, Co, Cu, Fe, Zn…)", v: "Below LOQ", limit: "Within limits" },
  { k: "Phthalate (DEHP)", v: "Below LOQ", limit: "≤ 1.5 mg/kg" },
];

const BADGES = [
  { icon: FlaskConical, t: "NABL / ISO 17025", d: "Tested at an accredited laboratory" },
  { icon: ShieldCheck, t: "IS 15410:2025", d: "Conforms to the Indian standard" },
  { icon: BadgeCheck, t: "Food-Grade Verified", d: "Migration & heavy-metal safe" },
];

export const Quality = () => (
  <section id="quality" data-testid="quality-section" className="relative bg-[var(--ink)] text-white overflow-hidden py-16 md:py-36">
    <div className="grain" />
    <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#5b8cff]">
            Quality & Compliance
          </span>
          <h2 className="font-display font-semibold tracking-tighter leading-[0.98] mt-5 text-4xl md:text-5xl">
            <MaskLineInView>Tested. Certified.</MaskLineInView>
            <MaskLineInView delay={0.08}>Every cycle.</MaskLineInView>
          </h2>
          <Reveal delay={0.12}>
            <p className="mt-6 text-lg leading-relaxed text-slate-300">
              Our caps undergo periodic testing at NABL-accredited laboratories to verify quality,
              safety, performance, and compliance with applicable Indian standards and regulatory
              requirements.
            </p>
          </Reveal>

          <div className="mt-8 space-y-3">
            {BADGES.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.t} delay={0.15 + i * 0.08}>
                  <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[var(--ink-soft)] p-4">
                    <div className="w-10 h-10 rounded-xl bg-[#5b8cff]/15 flex items-center justify-center text-[#5b8cff]">
                      <Icon size={20} />
                    </div>
                    <div>
                      <p className="font-display font-medium tracking-tight">{b.t}</p>
                      <p className="text-sm text-slate-400">{b.d}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-7">
          <Reveal y={50}>
            <div
              data-testid="sample-lab-report"
              className="rounded-3xl border border-white/10 bg-[var(--ink-soft)] overflow-hidden"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-7 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-[#5b8cff]/15 flex items-center justify-center text-[#5b8cff]">
                    <FileCheck2 size={22} />
                  </div>
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                      Sample Test Report
                    </p>
                    <p className="font-display text-lg font-medium tracking-tight">
                      ALASKA 27MM — HEXIQON Laboratory
                    </p>
                  </div>
                </div>
                <a
                  data-testid="download-lab-report"
                  href="/polycaps-lab-report.pdf"
                  download="Polycaps-Sample-Lab-Report.pdf"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[var(--ink)] hover:bg-[#5b8cff] hover:text-white active:scale-95 transition-[background-color,color,transform]"
                >
                  <Download size={16} /> Download report
                </a>
              </div>

              <div className="p-7">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500 font-mono text-[10px] uppercase tracking-[0.2em]">Report No.</p>
                    <p className="mt-1 text-slate-200">HL/MP/260702001</p>
                  </div>
                  <div>
                    <p className="text-slate-500 font-mono text-[10px] uppercase tracking-[0.2em]">ULR</p>
                    <p className="mt-1 text-slate-200 break-all">TC1171226000003730F</p>
                  </div>
                  <div>
                    <p className="text-slate-500 font-mono text-[10px] uppercase tracking-[0.2em]">Accreditation</p>
                    <p className="mt-1 text-slate-200">ISO/IEC 17025:2017</p>
                  </div>
                </div>

                <div className="mt-6 rounded-2xl border border-white/10 overflow-hidden">
                  {RESULTS.map((r, i) => (
                    <motion.div
                      key={r.k}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.06 }}
                      className={`flex items-center justify-between gap-4 p-4 ${
                        i % 2 ? "bg-transparent" : "bg-white/[0.03]"
                      }`}
                    >
                      <span className="text-slate-300">{r.k}</span>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="hidden sm:block text-xs text-slate-500 font-mono">{r.limit}</span>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#22c55e]/15 px-3 py-1 text-xs font-semibold text-[#4ade80]">
                          <BadgeCheck size={13} /> {r.v}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-slate-500">
                  Representative of the periodic testing every production batch is subject to. Full
                  parameters and limits are in the downloadable report.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
